/**
 * Remote-control surface (Phase E).
 *
 * All eight endpoints share the same response envelope. The optional
 * `force` flag bypasses the connector-state gate; we hoist it into the
 * query string so the body can stay clean.
 */

import { enc, ocppRequest } from './ocppApi';
import type {
  ChangeAvailabilityBody,
  DataTransferBody,
  GetDiagnosticsBody,
  RemoteControlResponse,
  ResetBody,
  TriggerMessageBody,
  UnlockConnectorBody,
  UpdateFirmwareBody,
} from '../models';

const stripForce = <T extends { force?: boolean }>(body: T): { body: Omit<T, 'force'>; force: boolean } => {
  const { force, ...rest } = body;
  return { body: rest as Omit<T, 'force'>, force: !!force };
};

export const changeAvailability = (chargerId: string, body: ChangeAvailabilityBody) => {
  const { body: payload, force } = stripForce(body);
  return ocppRequest<RemoteControlResponse>({
    method: 'POST',
    path: `availability/${enc(chargerId)}`,
    body: payload,
    query: force ? { force: 'true' } : undefined,
  });
};

export const resetCharger = (chargerId: string, body: ResetBody) => {
  const { body: payload, force } = stripForce(body);
  return ocppRequest<RemoteControlResponse>({
    method: 'POST',
    path: `reset/${enc(chargerId)}`,
    body: payload,
    query: force ? { force: 'true' } : undefined,
  });
};

export const unlockConnector = (chargerId: string, body: UnlockConnectorBody) => {
  const { body: payload, force } = stripForce(body);
  return ocppRequest<RemoteControlResponse>({
    method: 'POST',
    path: `unlock/${enc(chargerId)}`,
    body: payload,
    query: force ? { force: 'true' } : undefined,
  });
};

export const triggerMessage = (chargerId: string, body: TriggerMessageBody) => {
  const { body: payload, force } = stripForce(body);
  return ocppRequest<RemoteControlResponse>({
    method: 'POST',
    path: `trigger/${enc(chargerId)}`,
    body: payload,
    query: force ? { force: 'true' } : undefined,
  });
};

export const clearCache = (chargerId: string) =>
  ocppRequest<RemoteControlResponse>({
    method: 'POST',
    path: `clear-cache/${enc(chargerId)}`,
    body: {},
  });

export const dataTransfer = (chargerId: string, body: DataTransferBody) => {
  const { body: payload, force } = stripForce(body);
  return ocppRequest<RemoteControlResponse>({
    method: 'POST',
    path: `datatransfer/${enc(chargerId)}`,
    body: payload,
    query: force ? { force: 'true' } : undefined,
  });
};

export const getDiagnostics = (chargerId: string, body: GetDiagnosticsBody) => {
  const { body: payload, force } = stripForce(body);
  return ocppRequest<RemoteControlResponse>({
    method: 'POST',
    path: `diagnostics/${enc(chargerId)}`,
    body: payload,
    query: force ? { force: 'true' } : undefined,
  });
};

export const updateFirmware = (chargerId: string, body: UpdateFirmwareBody) => {
  const { body: payload, force } = stripForce(body);
  return ocppRequest<RemoteControlResponse>({
    method: 'POST',
    path: `firmware/${enc(chargerId)}`,
    body: payload,
    query: force ? { force: 'true' } : undefined,
  });
};
